import React, { useCallback, useEffect, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ApiDocsIcon from '../../../assets/api-docs-icon.svg';
import FbAddIcon from '../../../assets/fb-add-icon.svg';
import FbUnlinkIcon from '../../../assets/fb-unlink-icon.svg';
import HamburgerIcon from '../../../assets/HamburgerIcon';
import PhoneIcon from '../../../assets/PhoneIcon';
import SandboxIcon from '../../../assets/sandbox-icon.svg';
import SearchIcon from '../../../assets/SearchIcon';
import SandboxModal from '../../../components/WhatsApp/SandboxModal';
import UnlinkAccountModal from '../../../components/WhatsApp/UnlinkAccountModal';
import { getRequestAsync, postRequestAsync } from '../../../helpers/apiHandler';
import { useWhatsAppData } from '../../../hooks/useWhatsAppData';
import { showAlert } from '../../../store/actions/alertAction';
import './sidebar.scss';
import config from '../../../config/Constant';

const whatsappDocsURL = config.whatsapp_docs;

const SidebarDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { waba_id, phone_number_id } = useParams();
  const { access_token } = useSelector((state) => state.auth.user);
  const { data, error, refetch } = useWhatsAppData(
    `get-phone-numbers?waba_id=${waba_id}`,
    access_token?.token
  );
  const [templates, setTemplates] = useState(null);
  const [showActionsPopover, setShowActionsPopover] = useState(false);
  const [actionsTarget, setActionsTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [showSandboxModal, setShowSandboxModal] = useState(false);
  const [isCheckingSandbox, setIsCheckingSandbox] = useState(false);

  const handleActionsClick = (event) => {
    setShowActionsPopover(!showActionsPopover);
    setActionsTarget(event.target);
  };

  const handleUnlinkClick = () => {
    setShowActionsPopover(false);

    const activePhone =
      data?.data?.data?.data.find((item) => item.id === phone_number_id) ||
      data?.data?.data?.data[0];

    if (activePhone) {
      setSelectedPhoneNumber(activePhone);
      setShowDeleteModal(true);
    } else {
      console.error('No phone number available to unlink');
    }
  };

  const handleUnlink = (result) => {
    setShowDeleteModal(false);

    if (result.success) {
      dispatch(showAlert(result.message, 'success'));
      setTimeout(() => {
        navigate('/dashboard/whatsapp/getting-started');
      }, 500);
    }
  };

  const handleOpenSandbox = useCallback(
    async (e) => {
      if (e) e.preventDefault();

      if (isCheckingSandbox) return;

      try {
        setIsCheckingSandbox(true);

        const response = await getRequestAsync('/v2/whatsapp/sandbox-status', {
          headers: { Authorization: access_token.token },
        });

        if (response && !response.error && response.data && response.data.sandbox_active) {
          navigate('/dashboard/whatsapp/sandbox');
        } else {
          setShowSandboxModal(true);
        }
      } catch (error) {
        console.error('Error checking sandbox status:', error);
        dispatch(showAlert('Failed to check sandbox status', 'danger'));
        setShowSandboxModal(true);
      } finally {
        setIsCheckingSandbox(false);
      }
    },
    [access_token, dispatch, isCheckingSandbox, navigate]
  );

  const handleCloseSandbox = () => {
    setShowSandboxModal(false);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      if (templates) {
        return;
      }

      try {
        const response = await postRequestAsync(
          '/v2/whatsapp/get-phone-templates',
          {
            phone_number_id,
            waba_id,
            cursor: null,
          },
          access_token?.token
        );
        setTemplates(response);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    if (phone_number_id && waba_id && access_token?.token) {
      fetchTemplates();
    }
  }, [phone_number_id, waba_id, access_token?.token]);

  if (error) {
    return (
      <div className="sidebar-details">
        <h3 className="sidebar-details-title">Account List</h3>
        <div className="error-state p-3">
          Error loading data. Please try again.
          <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => refetch()}>
            Retry
          </button>
        </div>

        <div className="sidebar-bottom">
          <a href={whatsappDocsURL} target='_blank' rel='noreferrer' className="sidebar-btn api-docs-btn">
            API Docs
            <img src={ApiDocsIcon} alt="API Docs" />
          </a>

          <button className="sidebar-btn sandbox-btn" onClick={handleOpenSandbox}>
            Sandbox
            <img src={SandboxIcon} alt="Sandbox" />
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="sidebar-details">
        <h3 className="sidebar-details-title">Account List</h3>
        <div className="search-container">
          <div className="search-form">
            <SearchIcon />
            <input type="text" placeholder="Search" disabled />
          </div>
          <button
            className="btn btn-primary btn-add"
            disabled
            style={{ padding: '10px', fontSize: '16px !important' }}
          >
            <HamburgerIcon />
          </button>
        </div>

        <div className="loading-skeleton p-3">
          <div className="skeleton-item"></div>
          <div className="skeleton-item"></div>
          <div className="skeleton-item"></div>
        </div>

        <div className="sidebar-bottom">
          <a href={whatsappDocsURL} target='_blank' rel='noreferrer' className="sidebar-btn api-docs-btn">
            API Docs
            <img src={ApiDocsIcon} alt="API Docs" />
          </a>

          <button className="sidebar-btn sandbox-btn" onClick={handleOpenSandbox}>
            Sandbox
            <img src={SandboxIcon} alt="Sandbox" />
          </button>
        </div>
      </div>
    );
  }

  const phoneNumbers = data?.data?.data?.data || [];

  return (
    <div className="sidebar-details">
      <h3 className="sidebar-details-title">Account List</h3>
      <div className="search-container">
        <div className="search-form">
          <SearchIcon />
          <input type="text" placeholder="Search" />
        </div>
        <button
          className="btn btn-primary btn-add"
          onClick={handleActionsClick}
          style={{ padding: '10px', fontSize: '16px !important' }}
        >
          <HamburgerIcon />
        </button>
      </div>

      <div className="sidebar-content">
        {phoneNumbers.length > 0 ? (
          <ul className="list-unstyled">
            {phoneNumbers.map((item, index) => (
              <li key={index} className="phone-item">
                <Link
                  to={`/dashboard/whatsapp/phone-number-details/${waba_id}/${item.id}`}
                  className={`d-flex align-items-center phone-item-link ${
                    phone_number_id === item.id ? 'active' : ''
                  }`}
                >
                  <span className="phone-item-details">
                    <div className="phone-name">{item.verified_name}</div>
                    <div className="phone-number">
                      <PhoneIcon />
                      {item.display_phone_number}
                    </div>
                  </span>
                  <span className="template-count">{templates?.data?.data?.data?.length || 0}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="empty-state p-3">
            <p>No phone numbers found. Add a new number to get started.</p>
          </div>
        )}
      </div>

      <Overlay
        show={showActionsPopover}
        target={actionsTarget}
        placement="bottom-end"
        rootClose
        onHide={() => setShowActionsPopover(false)}
      >
        <Popover id="actions-popover" className="actions-popover">
          <Popover.Content className="p-0">
            <div className="actions-menu">
              <Link
                to="/dashboard/whatsapp/getting-started/"
                className="action-item add-number"
                onClick={() => setShowActionsPopover(false)}
              >
                <img src={FbAddIcon} alt="Add" className="action-icon" />
                <span>Add New Number</span>
              </Link>
              <button className="action-item unlink-account" onClick={handleUnlinkClick}>
                <img src={FbUnlinkIcon} alt="Unlink" className="action-icon" />
                <span>Unlink Facebook Account</span>
              </button>
            </div>
          </Popover.Content>
        </Popover>
      </Overlay>

      <UnlinkAccountModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        phoneNumberName={selectedPhoneNumber?.verified_name || 'Ellen elfo'}
        onDelete={handleUnlink}
        wabaId={waba_id}
      />

      <div className="sidebar-bottom">
        <a href={whatsappDocsURL} target='_blank' rel='noreferrer' className="sidebar-btn api-docs-btn">
          API Docs
          <img src={ApiDocsIcon} alt="API Docs" />
        </a>

        <button className="sidebar-btn sandbox-btn" onClick={handleOpenSandbox}>
          Sandbox
          <img src={SandboxIcon} alt="Sandbox" />
        </button>

        <SandboxModal isOpen={showSandboxModal} onClose={handleCloseSandbox} />
      </div>
    </div>
  );
};

export default SidebarDetails;
